import { jsx as _jsx } from "react/jsx-runtime";
import { memo, useEffect, useMemo } from 'react';
import { useHeatingCircuitTargetThermometer, useZ3KBoilerAdapterState, useZ3KDeviceState, } from 'console/shared/hooks/device/z3k';
import { BoilerAdapter, get_z3k_object_state, HeatingCircuit } from 'z3k-config';
import { useDeviceConfig } from 'console/shared/contexts/device/config';
import { BoilerHeatingCircuitContent } from './types';
import { otHasError } from 'console/shared/components/banners/failure/components/fail-card/utils';
import { boilerControl, BoilerControlIcon } from '../components/control-mode';
import { useDeviceState } from 'console/shared/contexts/device/state';
import { useHeatingDeviceEntitiesConfig } from 'console/shared/contexts/heating-device/types';
import { getWaterSensor } from 'shared/components/panels/heating-circuits/heating-circuit/components/status-line/components/indicators/water';
import { isCircuitWithBoilerAdapter } from 'z3k-parser/lib/heating-circuit';
function getBoilerStateText(is_fail, heating_circuit, heating_circuit_state) {
    if (!heating_circuit_state) {
        return 'Неизвестно';
    }
    if (HeatingCircuit.isTurnedOff(heating_circuit_state)) {
        return 'Выкл';
    }
    if (is_fail) {
        return 'АВАРИЯ';
    }
    if (HeatingCircuit.isBoilerReserve(heating_circuit_state)) {
        return 'Резерв';
    }
    if (HeatingCircuit.isAlwaysHeatRequest(heating_circuit)) {
        return 'Вкл';
    }
    return 'По запросу';
}
export const Z3KBoilerHeatingCircuitProvider = memo(function Z3KBoilerHeatingCircuitProvider(props) {
    const { children, setOpenThermStates, boiler_heating_circuit_config } = props;
    const { id, z3k_circuit: circuit, boiler_model, boiler_type, not_available_ot_flags, } = boiler_heating_circuit_config;
    const z3k_circuit = circuit || {};
    const { control_devices } = z3k_circuit;
    const { boiler_heating_modes } = useHeatingDeviceEntitiesConfig();
    const { z3k_config } = useDeviceConfig();
    const { is_service_mode } = useDeviceState();
    const z3k_state = useZ3KDeviceState();
    const boiler_adapter = BoilerAdapter.get.one(z3k_config, ({ id }) => {
        if (control_devices) {
            return control_devices.includes(id);
        }
        return false;
    });
    const z3k_heating_circuit_state = useMemo(() => {
        const state = get_z3k_object_state(z3k_state, id);
        if (state) {
            return state;
        }
        return {};
    }, [z3k_state, id]);
    const { ot: ot_state, status: ba_status } = useZ3KBoilerAdapterState(boiler_adapter?.id);
    const { target_sensor_id, setpoint_temp: setpoint_temperature, mode_id, } = z3k_heating_circuit_state;
    const target_thermometer = useHeatingCircuitTargetThermometer(z3k_config, target_sensor_id);
    const boiler_adapter_state = useMemo(() => {
        if (boiler_adapter) {
            const { id, name } = boiler_adapter;
            return {
                id,
                name,
                ot_state,
                ba_status,
            };
        }
        return null;
    }, [boiler_adapter, ot_state, ba_status]);
    const is_fail = useMemo(() => HeatingCircuit.isFail(z3k_heating_circuit_state) ||
        otHasError(boiler_model, ot_state, ba_status), [z3k_heating_circuit_state, boiler_model, ot_state, ba_status]);
    const boiler_state = useMemo(() => getBoilerStateText(is_fail, z3k_circuit, z3k_heating_circuit_state), [is_fail, z3k_circuit, z3k_heating_circuit_state]);
    const control_mode = useMemo(() => boilerControl(z3k_circuit, z3k_config, z3k_state).control_mode, [z3k_circuit, z3k_state, z3k_config]);
    const control_icon = useMemo(() => (_jsx(BoilerControlIcon, { hc: z3k_circuit, z3k_config: z3k_config, color: is_fail && is_service_mode ? 'inherit' : 'action', "data-tooltip": "control-icon" })), [z3k_circuit, z3k_config, is_fail, is_service_mode]);
    const heating_mode = useMemo(() => boiler_heating_modes?.find(({ id }) => mode_id === id), [boiler_heating_modes, mode_id]);
    const ot_boiler_state = useMemo(() => {
        if (isCircuitWithBoilerAdapter(z3k_config, z3k_circuit)) {
            return boiler_adapter_state;
        }
        return null;
    }, [z3k_config, z3k_circuit]);
    useEffect(() => {
        if (ot_boiler_state && boiler_adapter) {
            setOpenThermStates((states) => {
                if (states.some((state) => state.id == id)) {
                    return states.map((state) => {
                        if (state.id == id) {
                            return {
                                id,
                                boiler_type,
                                not_available_ot_flags,
                                ot_boiler_state,
                                ba_status,
                            };
                        }
                        else {
                            return state;
                        }
                    });
                }
                return [
                    ...states,
                    {
                        id,
                        boiler_type,
                        not_available_ot_flags,
                        ot_boiler_state,
                        ba_status,
                    },
                ];
            });
        }
    }, [ot_boiler_state, id, boiler_type, not_available_ot_flags]);
    const { sensor: reserve_sensor } = getWaterSensor(z3k_config, z3k_state, z3k_circuit && z3k_circuit.water_temp_sensor_reserve);
    const boiler_heating_circuit_state = useMemo(() => ({
        mode_id,
        is_fail,
        setpoint_temperature,
        target_thermometer,
        boiler_state,
        heating_mode,
        ot_boiler_state,
        control_icon,
        control_mode,
        reserve_sensor,
    }), [
        mode_id,
        is_fail,
        setpoint_temperature,
        target_thermometer,
        boiler_state,
        heating_mode,
        ot_boiler_state,
        control_icon,
        control_mode,
        reserve_sensor,
    ]);
    const boiler_heating_circuit = useMemo(() => ({
        ...boiler_heating_circuit_state,
        ...boiler_heating_circuit_config,
    }), [boiler_heating_circuit_state, boiler_heating_circuit_config]);
    return (_jsx(BoilerHeatingCircuitContent.Provider, { value: boiler_heating_circuit, children: children }));
});
